import { STATUS } from 'Utils/styleTokens';

/**
 * ONE risk vocabulary for every screen that shows a source's discovery risk level.
 *
 * The platform scores a discovered source 0–100 and bands it LOW ≤19 · MED_LOW ≤39 · MEDIUM ≤59 ·
 * MEDIUM_HIGH ≤74 · HIGH ≤89 · CRITICAL, or UNKNOWN when it has no score. The UI folds those six
 * bands into four tiers plus "not yet discovered", with one label and one colour each, so the
 * Workflow header, the pipeline cards, the risk profile and the discovery ledger all agree.
 * (The Enterprise Data Landscape's severity is an operator-configured scale over finding counts
 * — a different measure — but it uses the same hues, so the two read as one system.)
 */
export const RISK_TIERS = [
  { key: 'critical', label: 'Critical', ...STATUS.danger },
  { key: 'high', label: 'High', ...STATUS.orange },
  { key: 'medium', label: 'Medium', ...STATUS.warning },
  { key: 'low', label: 'Low', ...STATUS.info },
  { key: 'unknown', label: 'Not yet discovered', ...STATUS.neutral },
];

const BY_KEY = RISK_TIERS.reduce((acc, t) => ({ ...acc, [t.key]: t }), {});

export const riskTierKey = (level) => {
  const l = String(level || '').toUpperCase();
  if (l === 'CRITICAL' || l === 'PHI') return 'critical';
  if (l === 'HIGH' || l === 'MEDIUM_HIGH' || l === 'VERY_HIGH') return 'high';
  if (l === 'MEDIUM' || l === 'MED_LOW') return 'medium';
  if (l === 'LOW') return 'low';
  return 'unknown';
};

/** { key, label, text, bg, dot } for a server risk level (any case; null → unknown). */
export const riskTier = (level) => BY_KEY[riskTierKey(level)];

/** Counts per tier key — ALWAYS all five keys, zeros included, so a strip never hides a tier. */
export const riskTierCounts = (rows) => {
  const counts = { critical: 0, high: 0, medium: 0, low: 0, unknown: 0 };
  (rows || []).forEach((r) => {
    counts[riskTierKey(r && r.latestRiskLevel)] += 1;
  });
  return counts;
};

/**
 * The legacy 1–6 band a few older panels still index (Utils RISK_LEVELS categories / colours).
 * Derived from the LEVEL, never from the 0–100 score — the two scales are unrelated.
 */
export const legacyBandOf = (level) => {
  const l = String(level || '').toUpperCase();
  if (l === 'CRITICAL' || l === 'PHI') return 6;
  if (l === 'HIGH' || l === 'VERY_HIGH') return 5;
  if (l === 'MEDIUM_HIGH') return 4;
  if (l === 'MEDIUM') return 3;
  if (l === 'MED_LOW' || l === 'LOW') return 2;
  return 1;
};
